import { ElMessage, ElLoading } from 'element-plus'
import { emitAuthExpired } from '@/util/auth-events'
import router from '@/router'
import { storageGet, storageSet, storageRemove, StorageKeys } from '@/util/storage'

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL
export const TOKEN_KEY = 'X-Admin-Access-Token'

function urlencodeArray(key, arr) {
  let str = ''
  for (let el of arr) {
    str += `${getEncodedValue(key)}=${getEncodedValue(el)}&`
  }
  return str.slice(0, -1)
}

function getEncodedValue(value) {
  if (value === null || value === undefined) {
    return ''
  } else {
    return encodeURIComponent(value)
  }
}

export function saveLoginInfo(loginInfo) {
  storageSet(StorageKeys.LOGIN_INFO, JSON.stringify(loginInfo))
}

export function getLoginInfo() {
  const raw = storageGet(StorageKeys.LOGIN_INFO)
  return raw ? JSON.parse(raw) : null
}

export function getAccessToken() {
  return getLoginInfo() ? getLoginInfo().accessToken : null
}

export async function isAuthenticated() {
  const accessToken = getAccessToken()
  if (!accessToken) {
    return false
  }

  const now = new Date().getTime()
  const lastCheckTime = storageGet(StorageKeys.SESSION_CHECK_TIME)
  if (lastCheckTime && now - parseInt(lastCheckTime) < 600000) {
    return true
  }

  const response = await fetch(`${API_BASE_URL}checkSession`, { headers: { [TOKEN_KEY]: accessToken } })
  const { code } = await normalizeResponse(response)
  if (code === 0) {
    storageSet(StorageKeys.SESSION_CHECK_TIME, String(now))
    return true
  } else {
    clearLoginInfo()
    return false
  }
}

export function clearLoginInfo() {
  storageRemove(StorageKeys.LOGIN_INFO)
  storageRemove(StorageKeys.SESSION_CHECK_TIME)
}

export function urlencoded(obj) {
  let str = ''
  for (const key in obj) {
    let value = obj[key]
    if (Array.isArray(value)) {
      str += urlencodeArray(key, value) + '&'
    } else {
      str += `${getEncodedValue(key)}=${getEncodedValue(value)}&`
    }
  }
  return str.slice(0, -1)
}

function normalizeRequest(options) {
  let { url, method = 'GET', headers = {}, query = {}, body = null, sendToken = true, ...restOptions } = options

  const queryStr = urlencoded(query)
  if (queryStr) {
    url += '?' + queryStr
  }

  if (sendToken) {
    headers[TOKEN_KEY] = headers[TOKEN_KEY] || getAccessToken()
  }

  let contentType = headers['Content-Type']
  if (contentType === false) {
    //特殊处理:文件上传不能设置content-type
    delete headers['Content-Type']
  } else {
    contentType = contentType || 'application/x-www-form-urlencoded'
    headers['Content-Type'] = contentType
  }

  if (method !== 'GET' && body) {
    if (contentType === 'application/json') {
      body = JSON.stringify(body)
    } else if (contentType === 'application/x-www-form-urlencoded') {
      body = urlencoded(body)
    }
  } else {
    body = null
  }

  return { url, method, headers, body, ...restOptions }
}

async function normalizeResponse(response) {
  const raw = await response.text()
  const noContent = raw === null || raw === undefined || (typeof raw === 'string' && raw.trim() === '')
  if (noContent) {
    return { code: -1, data: null, message: '服务器返回的数据格式异常' }
  }
  try {
    const { code, data, message } = typeof raw === 'string' ? JSON.parse(raw.trim()) : raw
    return { code, data, message }
  } catch (err) {
    console.error(err)
    return { code: -1, data: raw, message: '服务器返回的数据格式异常' }
  }
}

function safeCall(fn, ...args) {
  if (!fn) return
  try {
    fn(...args)
  } catch (err) {
    console.error(err)
  }
}

/**
 * @typedef {Object} HttpRequestBaseOptions
 * @property {string} url 相对路径，与 `API_BASE_URL` 拼接；`query` 编码为查询串追加
 * @property {string} [method='GET']
 * @property {Record<string, string|boolean>} [headers={}] `Content-Type`：`application/json`、`application/x-www-form-urlencoded`，或 `false`（上传）
 * @property {Record<string, *>} [query={}]
 * @property {*} [body=null] 非 GET 且有值时序列化写入请求体（默认表单编码）
 * @property {boolean} [sendToken=true] 自动写 `TOKEN_KEY`，已存在则不覆盖
 * @property {boolean} [loading=false]
 * @property {HTMLElement|null} [loadingRef=null] 不传则全屏遮罩
 */

/**
 * 基于 `fetch` 的回调式请求。响应 JSON `{ code, data, message }`：`code === 0` 成功；`code === 100011` 会清登录并 `emitAuthExpired()`。
 * 成功调 `success(data)`（未传则无提示）；失败调 `fail({ code, data, message })`，否则 `rollback` + `ElMessage.error`；`complete` 始终执行。
 * @param {HttpRequestBaseOptions & {
 *   success?: (data: *) => void,
 *   fail?: (payload: { code: number, data: *, message: string }) => void,
 *   complete?: () => void,
 *   rollback?: () => void
 * }} options
 * @returns {Promise<void>}
 */
export async function newRequest(options) {
  const { url, method, headers, body, loading, loadingRef, success, fail, complete, rollback } = normalizeRequest(options)

  let loadingHandle = null
  if (loading) {
    loadingHandle = ElLoading.service({
      target: loadingRef || document.body,
      fullscreen: !loadingRef,
      lock: true,
      text: '加载中...',
      background: 'rgba(255, 255, 255, 0.5)'
    })
  }

  const handleError = ({ code, data, message }) => {
    if (fail) {
      safeCall(fail, { code, data, message })
      return
    }
    safeCall(rollback)
    ElMessage.error(message)
  }

  try {
    const response = await fetch(`${API_BASE_URL}${url}`, { method, headers, body })
    const { code, data, message } = await normalizeResponse(response)
    if (code === 100011) {
      clearLoginInfo()
      emitAuthExpired()
    }
    if (code === 0) {
      safeCall(success, data)
    } else {
      handleError({ code, data, message })
    }
  } catch (err) {
    console.error(err)
    handleError({ code: -1, data: err, message: '客户端请求异常' })
  } finally {
    if (loading) loadingHandle.close()
    safeCall(complete)
  }
}

/**
 * 基于 `fetch` 的 Promise 式请求，返回 `{ code, data, message }`，不 `throw`。约定同 `newRequest`（含 `100011` 处理）；异常为 `code: -1`、`message: '客户端请求异常'`。
 * @param {HttpRequestBaseOptions} options
 * @returns {Promise<{ code: number, data: *, message: string }>}
 */
export async function newCall(options) {
  const { url, method, headers, body, loading, loadingRef } = normalizeRequest(options)

  let loadingHandle = null
  if (loading) {
    loadingHandle = ElLoading.service({
      target: loadingRef || document.body,
      fullscreen: !loadingRef,
      lock: true,
      text: '加载中...',
      background: 'rgba(255, 255, 255, 0.5)'
    })
  }

  try {
    const response = await fetch(`${API_BASE_URL}${url}`, { method, headers, body })
    const result = await normalizeResponse(response)
    if (result.code === 100011) {
      clearLoginInfo()
      emitAuthExpired()
    }
    return result
  } catch (err) {
    console.error(err)
    return { code: -1, data: err, message: '客户端请求异常' }
  } finally {
    if (loading) loadingHandle.close()
  }
}

export async function request({ url, options, fn, rawResponse, loading, loadingRef, sync }) {
  options = options || {}
  options.headers = options.headers || {}
  options.headers[TOKEN_KEY] = options.headers[TOKEN_KEY] || getAccessToken()
  options.method = options.method || 'GET'
  if (options.query) url += '?' + urlencoded(options.query)
  if (options.method.toUpperCase() != 'GET' && options.body) {
    let contentType = options.headers['Content-Type']
    if (contentType === false) {
      //特殊处理:文件上传不能设置content-type
      delete options.headers['Content-Type']
    } else {
      contentType = contentType || 'application/x-www-form-urlencoded'
      options.headers['Content-Type'] = contentType
    }
    if (contentType === 'application/json') {
      options.body = JSON.stringify(options.body)
    } else if (contentType === 'application/x-www-form-urlencoded') {
      options.body = urlencoded(options.body)
    }
  }

  let loadingHandle = null
  if (loading) {
    loadingHandle = ElLoading.service({
      target: loadingRef ? loadingRef : document.body,
      fullscreen: !loadingRef,
      lock: true,
      text: '加载中...',
      background: 'rgba(255, 255, 255, 0.5)'
    })
  }

  if (sync) {
    try {
      let response = await fetch(`${API_BASE_URL}${url}`, options)
      if (response.status === 401) {
        clearLoginInfo()
        ElMessage.error('登录信息过期，请重新登录')
        router.push('/login')
      } else if (response.status === 403) {
        ElMessage.error('权限不足')
      } else if (response.status === 404) {
        ElMessage.error('服务器接口不存在')
      } else if (response.status === 500) {
        ElMessage.error('服务器异常')
      } else if (response.ok) {
        if (rawResponse) return response
        return response.json()
      } else {
        if (response.body) {
          return response.json()
        } else {
          return Promise.reject({ message: '服务器返回异常' })
        }
      }
    } finally {
      if (loading) loadingHandle.close()
    }
  } else {
    fetch(`${API_BASE_URL}${url}`, options)
      .then((response) => {
        if (loading) loadingHandle.close()
        if (response.status === 400 || response.status === 404) {
          return response.json()
        } else if (response.status === 401) {
          clearLoginInfo()
          ElMessage.error('登录信息过期，请重新登录')
          router.push('/login')
        } else if (response.status === 403) {
          ElMessage.error('权限不足')
        } else if (response.status === 500) {
          ElMessage.error('服务器异常')
        } else if (response.ok) {
          if (rawResponse) return response
          return response.json()
        } else {
          ElMessage.error('服务器返回异常')
        }
      })
      .then((data) => {
        if (fn && data) fn(data)
      })
      .catch((error) => {
        console.error(error)
        ElMessage.error('发生未知错误')
      })
  }
}

export default {
  API_BASE_URL,
  TOKEN_KEY,
  saveLoginInfo,
  getLoginInfo,
  getAccessToken,
  isAuthenticated,
  clearLoginInfo,
  urlencoded,
  newRequest,
  newCall,
  request
}
